import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import axios from 'axios';
import * as mammoth from 'mammoth';
import {
    Box, Typography, Grid, Button, CircularProgress, Container, Card,
    Dialog, DialogTitle, DialogContent, IconButton, alpha, Divider, Stack
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const COLORS = {
    primary: '#1e293b',
    secondary: '#f59e0b',
    cardBg: '#ffffff',
    pageBg: '#f8fafc'
};

const TemplatesViewer = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewDialog, setViewDialog] = useState({ open: false, url: '', type: '', title: '', fileUrl: '' });
    const [docxHtml, setDocxHtml] = useState('');
    const [pdfBlobUrl, setPdfBlobUrl] = useState(''); 
    const [loadingPreview, setLoadingPreview] = useState(false);

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const res = await axios.get(`${API_BASE_URL}/api/templates`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setTemplates(res.data);
            } catch (err) {
                console.error("Failed to fetch templates", err);
            } finally {
                setLoading(false);
            }
        };
        fetchTemplates();
    }, []);

    const getOriginalFileName = (fileUrl) => {
        if (!fileUrl) return 'Untitled_Document';
        const parts = fileUrl.split('/').pop().split('-');
        return parts.length >= 3 ? parts.slice(2).join('-') : fileUrl.split('/').pop();
    };

    const handleDownload = async (fileUrl) => {
        try {
            const token = localStorage.getItem('jwtToken');
            const targetUrl = fileUrl.startsWith('http') ? fileUrl : `${API_BASE_URL}/${fileUrl}`;
            const response = await axios.get(targetUrl, {
                responseType: 'blob',
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const fileName = getOriginalFileName(fileUrl);
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed", error);
            const targetUrl = fileUrl.startsWith('http') ? fileUrl : `${API_BASE_URL}/${fileUrl}`;
            window.open(targetUrl, '_blank');
        }
    };

    const fetchPdfPreview = async (fileUrl) => {
        setLoadingPreview(true);
        setPdfBlobUrl('');
        try {
            const token = localStorage.getItem('jwtToken');
            const targetUrl = fileUrl.startsWith('http') ? fileUrl : `${API_BASE_URL}/${fileUrl}`;
            const response = await axios.get(targetUrl, {
                responseType: 'blob',
                headers: { Authorization: `Bearer ${token}` }
            });
            const file = new Blob([response.data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            setPdfBlobUrl(fileURL);
        } catch (error) {
            console.error("Failed to load PDF preview", error);
        } finally {
            setLoadingPreview(false);
        }
    };

    const convertDocxToHtml = async (fileUrl) => {
        setLoadingPreview(true);
        setDocxHtml('');
        try {
            const token = localStorage.getItem('jwtToken');
            const targetUrl = fileUrl.startsWith('http') ? fileUrl : `${API_BASE_URL}/${fileUrl}`;
            const response = await axios.get(targetUrl, {
                responseType: 'arraybuffer',
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const result = await mammoth.convertToHtml({ arrayBuffer: response.data });
            setDocxHtml(DOMPurify.sanitize(result.value));
        } catch (error) {
            console.error("Failed to convert document", error);
            setDocxHtml('<p style="color: red; text-align: center;">Failed to load document preview. Please download the file to view it.</p>');
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleView = (template) => {
        const isDocx = template.file_type?.includes('word') || 
                       template.file_type?.includes('document') || 
                       template.file_url?.endsWith('.docx');
        
        const isPdf = template.file_type?.includes('pdf') || 
                      template.file_url?.endsWith('.pdf');

        setViewDialog({ 
            open: true, 
            url: template.file_url.startsWith('http') ? template.file_url : `${API_BASE_URL}/${template.file_url}`, 
            title: template.heading,
            type: template.file_type || (isPdf ? 'application/pdf' : 'application/msword'),
            fileUrl: template.file_url
        });

        if (isDocx) {
            convertDocxToHtml(template.file_url);
        } else if (isPdf) {
            fetchPdfPreview(template.file_url);
        }
    };

    const handleCloseDialog = () => {
        setViewDialog({ ...viewDialog, open: false });
        if (pdfBlobUrl) {
            URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl('');
        }
    };

    const renderThumbnail = (template) => {
        const isPdf = template.file_type?.includes('pdf') || template.file_url?.endsWith('.pdf');
        const color = isPdf ? '#ef4444' : '#3b82f6';

        return (
            <Box sx={{ 
                display: 'flex', flexDirection: 'column', alignItems: 'center', 
                justifyContent: 'center', height: '100%', bgcolor: '#f1f5f9',
            }}>
                {isPdf ? <PictureAsPdfIcon sx={{ fontSize: 48, color }} /> : <DescriptionIcon sx={{ fontSize: 48, color }} />}
                <Typography sx={{ 
                    fontWeight: 600, mt: 1.5, color, 
                    letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.75rem' 
                }}>
                    {isPdf ? 'PDF Document' : 'Word Document'}
                </Typography>
            </Box>
        );
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress sx={{ color: COLORS.primary }} />
        </Box>
    );

    return (
        <Box sx={{ 
            minHeight: '100vh', bgcolor: COLORS.pageBg, pt: 1, pb: 8,
            fontFamily: "'Inter', 'Roboto', sans-serif" 
        }}>
            <Container maxWidth="xl">
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <Typography sx={{ color: COLORS.secondary, fontWeight: 700, letterSpacing: '3px', fontSize: '1rem', textTransform: 'uppercase' }}>
                        Library
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.primary, mt: 0.5, fontSize: '1.5rem' }}>
                        ShipperDock Templates
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '1rem', mt: 0.5, fontWeight: 500 }}>
                        Free downloadable templates 
                    </Typography>
                </Box>

                <Box sx={{ px: { xs: 2, md: 4 } }}>
                    <Grid container spacing={4} sx={{ justifyContent: 'flex-start' }}>
                        {templates.map((template) => {
                            const realFileName = getOriginalFileName(template.file_url);
                            return (
                                <Grid item key={template.id}>
                                    <Card 
                                        elevation={0}
                                        sx={{ 
                                            width: 250,
                                            height: 380, 
                                            borderRadius: '20px',
                                            border: '1px solid #e2e8f0',
                                            background: COLORS.cardBg,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-6px)',
                                                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)',
                                            }
                                        }}
                                    >
                                        <Box sx={{ height: 160, overflow: 'hidden', m: 1.5, borderRadius: '14px' }}>
                                            {renderThumbnail(template)}
                                        </Box>

                                        <Box sx={{ px: 2.5, py: 1, flexGrow: 1 }}>
                                            <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: COLORS.primary, mb: 0.5 }}>
                                                {template.heading}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', display: 'block', noWrap: true, overflow: 'hidden', textOverflow: 'ellipsis', mb: 2 }}>
                                                {realFileName}
                                            </Typography>
                                            
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography sx={{ color: COLORS.secondary, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                                                    Added
                                                </Typography>
                                                <Typography sx={{ color: COLORS.primary, fontWeight: 600, fontSize: '0.85rem' }}>
                                                    • {new Date(template.created_at).toLocaleDateString('en-US', { 
                                                        month: 'short', 
                                                        day: 'numeric', 
                                                        year: 'numeric' 
                                                    })}
                                                </Typography>
                                            </Stack>
                                        </Box>

                                        <Divider sx={{ opacity: 0.5 }} />

                                        <Stack direction="row" sx={{ p: 1, alignItems: 'center' }}>
                                            <Button 
                                                fullWidth
                                                startIcon={<VisibilityIcon />}
                                                onClick={() => handleView(template)}
                                                sx={{ 
                                                    color: COLORS.primary, 
                                                    textTransform: 'none', 
                                                    fontWeight: 600,
                                                    fontSize: '0.9rem',
                                                    py: 1,
                                                    borderRadius: '12px',
                                                    '&:hover': { bgcolor: '#f1f5f9' }
                                                }}
                                            >
                                                View
                                            </Button>
                                            <Divider orientation="vertical" flexItem sx={{ height: 24, my: 'auto', mx: 0.5 }} />
                                            <IconButton 
                                                onClick={() => handleDownload(template.file_url)}
                                                sx={{ color: COLORS.secondary, p: 1.5, '&:hover': { bgcolor: alpha(COLORS.secondary, 0.1) } }}
                                            >
                                                <DownloadIcon />
                                            </IconButton>
                                        </Stack>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            </Container>

            <Dialog 
                open={viewDialog.open} 
                onClose={handleCloseDialog} 
                maxWidth="md" 
                fullWidth 
                PaperProps={{ sx: { borderRadius: '24px' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: COLORS.primary }}>
                        {viewDialog.title}
                    </Typography>
                    <IconButton onClick={handleCloseDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, height: '90vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc' }}>
                    
                    {/* --- PDF LOGIC STARTS HERE --- */}
                    {(viewDialog.type && viewDialog.type.includes('pdf')) || (viewDialog.fileUrl && viewDialog.fileUrl.endsWith('.pdf')) ? (
                         <Box sx={{ width: '100%', height: '100%', bgcolor: 'white', display: 'flex', flexDirection: 'column' }}>
                             {loadingPreview ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 2 }}>
                                    <CircularProgress sx={{ color: COLORS.primary }} />
                                    <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                                        Loading PDF preview...
                                    </Typography>
                                </Box>
                             ) : pdfBlobUrl ? (
                                <iframe 
                                    src={pdfBlobUrl} 
                                    width="100%" 
                                    height="100%" 
                                    style={{ border: 'none' }} 
                                    title="PDF Preview" 
                                />
                             ) : (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography color="error">Failed to load PDF.</Typography>
                                </Box>
                             )}
                         </Box>

                    /* --- DOCX LOGIC HERE --- */
                    ) : (viewDialog.type?.includes('word') || viewDialog.type?.includes('document') || viewDialog.fileUrl?.endsWith('.docx')) ? (
                        <Box sx={{ width: '100%', height: '100%', overflow: 'auto', bgcolor: 'white' }}>
                            {loadingPreview ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 2 }}>
                                    <CircularProgress sx={{ color: COLORS.primary }} />
                                    <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                                        Loading document preview...
                                    </Typography>
                                </Box>
                            ) : docxHtml ? (
                                <Box 
                                    sx={{ 
                                        p: 4, 
                                        maxWidth: '700px', 
                                        margin: '0 auto',
                                        '& img': { maxWidth: '100%', height: 'auto' },
                                        '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
                                        '& td, & th': { border: '1px solid #ddd', padding: '8px' },
                                        '& p': { mb: 1.5 },
                                        '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1 }
                                    }}
                                    dangerouslySetInnerHTML={{ __html: docxHtml }}
                                />
                            ) : (
                                <Box sx={{ textAlign: 'center', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        Unable to load preview
                                    </Typography>
                                    <Button 
                                        variant="contained" 
                                        startIcon={<DownloadIcon />}
                                        onClick={() => handleDownload(viewDialog.fileUrl)}
                                        sx={{ bgcolor: COLORS.secondary, mt: 2 }}
                                    >
                                        Download File
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                Preview not available
                            </Typography>
                            <Button 
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownload(viewDialog.fileUrl)}
                                sx={{ bgcolor: COLORS.secondary, mt: 2 }}
                            >
                                Download File
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default TemplatesViewer;